import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useAccess } from 'hooks/useAccess';
import { useHandleUploadOrder } from 'hooks/useHandlersContract';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { methodologyMapReverse } from 'types/methodology';

import noun from 'plural-ru';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TabsIds } from '../../../Common/types';

export const useCreateWorkOrder = (projectId?: string, contractData?: Record<string, any> | null) => {
    const { methodology } = useProjectParams();
    const navigate = useNavigate();

    const { projectData } = useProjectData(projectId, false);

    const typedContractMetaData = contractData?.metadata as {
        contractor: string;
        contractorAddress: string;
        hasContractChanges: boolean;
        hasFilledMandatoryProperties: boolean;
        isPriceCollectionLoaded: boolean;
        unreadyUerRowCount: number;
        hasFilledAddresses: boolean;
        hasEmptyFormulaInContract: boolean;
        hasEmptyOtherCostTypeInContract: boolean;
    };

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { order: orderRules } = useAccess();
    const handleUploadOrderClick = useHandleUploadOrder(projectId, methodology);

    const [isContractModalActive, setIsContractModalActive] = useState(false);
    const [isUnreadyModalActive, setIsUnreadyModalActive] = useState(false);

    const [resetProjectData] = useResetStoreData({ requestId: `getProject` });

    const modalCancelHandler = useCallback(() => {
        setIsContractModalActive(false);
        setIsUnreadyModalActive(false);
    }, []);

    const modalContractConfirmHandler = useCallback(() => {
        modalCancelHandler();
        navigate(`/projects/${methodology}/${projectId}/contract/edit/${TabsIds.COMMON}`);
        resetProjectData({});
    }, [methodology, modalCancelHandler, navigate, projectId, resetProjectData]);

    const modalUnreadyConfirmHandler = useCallback(() => {
        modalCancelHandler();

        navigate(`/projects/${methodology}/${projectId}/contract/prices/`);

        resetProjectData({});
    }, [methodology, modalCancelHandler, navigate, projectId, resetProjectData]);

    const isContractInformerVisible = useMemo(
        () => !(contractData?.hasFilledMandatoryProperties ?? typedContractMetaData?.hasFilledMandatoryProperties),
        [contractData?.hasFilledMandatoryProperties, typedContractMetaData?.hasFilledMandatoryProperties],
    );

    const isUnreadyUerModalVisible = useMemo(
        () =>
            !(contractData?.isPriceCollectionLoaded ?? typedContractMetaData?.isPriceCollectionLoaded) ||
            (contractData?.unreadyUerRowCount ?? typedContractMetaData?.unreadyUerRowCount) !== 0,
        [
            contractData?.isPriceCollectionLoaded,
            contractData?.unreadyUerRowCount,
            typedContractMetaData?.isPriceCollectionLoaded,
            typedContractMetaData?.unreadyUerRowCount,
        ],
    );

    const isHasFilledAddresses = useMemo(
        () => contractData?.hasFilledAddresses || typedContractMetaData?.hasFilledAddresses,
        [contractData?.hasFilledAddresses, typedContractMetaData?.hasFilledAddresses],
    );

    const hasEmpty = useMemo(
        () =>
            contractData?.hasEmptyFormulaInContract ||
            typedContractMetaData?.hasEmptyFormulaInContract ||
            contractData?.hasEmptyOtherCostTypeInContract ||
            typedContractMetaData?.hasEmptyOtherCostTypeInContract,
        [
            contractData?.hasEmptyFormulaInContract,
            contractData?.hasEmptyOtherCostTypeInContract,
            typedContractMetaData?.hasEmptyFormulaInContract,
            typedContractMetaData?.hasEmptyOtherCostTypeInContract,
        ],
    );

    const goToEdit = useCallback(() => {
        navigate(`/projects/uer/${projectId}/contract/${hasEmpty ? 'others' : 'edit'}`);
    }, [hasEmpty, navigate, projectId]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: goToEdit,
        });

    const uploadClickHandler = useCallback(() => {
        if (isContractInformerVisible) {
            setIsContractModalActive(true);
        } else if (isUnreadyUerModalVisible) {
            setIsUnreadyModalActive(true);
        } else if (!isHasFilledAddresses || hasEmpty) {
            handleOpenConfirmationDialog();
        } else {
            handleUploadOrderClick();
        }

        sendGpnAnalyticsEvent({
            componentId: 'rim_create order button_pressed',
            projectId,
            contractId: contractData?.id,
            methodology: methodology ? methodologyMapReverse[methodology] : undefined,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });
    }, [
        contractData?.id,
        handleOpenConfirmationDialog,
        handleUploadOrderClick,
        hasEmpty,
        isContractInformerVisible,
        isHasFilledAddresses,
        isUnreadyUerModalVisible,
        methodology,
        projectData?.contractType,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const unreadyUerMsg = useMemo(
        () =>
            `В прейскуранте расценок для ${
                contractData?.unreadyUerRowCount ?? typedContractMetaData?.unreadyUerRowCount
            } ${noun(
                contractData?.unreadyUerRowCount ?? typedContractMetaData?.unreadyUerRowCount,
                ...['расценки', 'расценок'],
            )} не заполнены Глава в расчете и Тип УЕР`,
        [contractData?.unreadyUerRowCount, typedContractMetaData?.unreadyUerRowCount],
    );

    const onCloseConfirmation = useCallback(() => {
        setTimeout(() => handleCloseConfirmationDialog());
    }, [handleCloseConfirmationDialog]);

    const errorTextAddresses = useMemo(() => {
        if (!contractData?.customerAddress && !contractData?.contractorAddress) {
            return "Для создания наряд-заказа необходимо заполнить поля договора 'Адрес заказчика' и 'Адрес подрядчика'";
        }
        if (!contractData?.customerAddress) {
            return "Для создания наряд-заказа необходимо заполнить поле договора 'Адрес заказчика'";
        }

        return "Для создания наряд-заказа необходимо заполнить поле договора 'Адрес подрядчика'";
    }, [contractData?.contractorAddress, contractData?.customerAddress]);

    const CreateWorkOrderConfirmationDialog = useMemo(() => {
        const textConfirmation = hasEmpty
            ? 'Для создания Наряд-Заказа заполните обязательные параметры прочих затрат'
            : errorTextAddresses;

        return (
            <ConfirmationDialog
                textHeader="СОЗДАНИЕ НАРЯД-ЗАКАЗА"
                textConfirmation={textConfirmation}
                cancelButtonText="Отменить"
                confirmButtonText="Заполнить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={onCloseConfirmation}
                preventButtonsDefaults
                styleError
            />
        );
    }, [errorTextAddresses, handleActionQuery, hasEmpty, isOpenConfirmationDialog, onCloseConfirmation]);

    return {
        orderRules,
        isContractModalActive,
        isUnreadyModalActive,
        unreadyUerMsg,
        modalContractConfirmHandler,
        modalUnreadyConfirmHandler,
        uploadClickHandler,
        modalCancelHandler,
        CreateWorkOrderConfirmationDialog,
    };
};
